import orderBy from 'lodash/orderBy';
import groupBy from 'lodash/groupBy';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import maxBy from 'lodash/maxBy';
import get from 'lodash/get';
import omit from 'lodash/omit';

import coefficients from '../../../shared/calculate-coefficients';
import queryToStat from '../../utils/query-to-stat';

const defaultInitialState = {};

const createTable = ({
	namespace,
	stateNodeName
}) => {
	// actions types

	const CHANGE_ORDER_DURATION = `${namespace}/${stateNodeName}-change-order-duration`;

	// initState
	const initialState = {
		...defaultInitialState,
		groupOrderDuration: 'desc'
	};

	// actions
	const actions = {
		groupOrder: () => ({
			type: CHANGE_ORDER_DURATION
		})
	};

	// reducers
	const reducers = {
		[CHANGE_ORDER_DURATION]: (state, action) => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName],
				groupOrderDuration: state[stateNodeName].groupOrderDuration === 'desc' ? 'asc' : 'desc'
			}
		})
	};

	// selectors
	const selectors = {
		getDate: state => {
			if (!state[namespace].data) return null;
			const { data, cellConfigFilter } = state[namespace];
			const groups = state[namespace].grouping.values;
			const orderDuration = state[namespace][stateNodeName]['groupOrderDuration'];
			const header = data.periods;
			const rows = getBodyRows(
				data,
				header,
				groups,
				cellConfigFilter,
				orderDuration,
				state[namespace]
			);
			const { activeCell } = normalizedCellConfig(cellConfigFilter);

			return { header, rows, activeCell, orderDuration };
		}
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions
const getBodyRows = (
	{data},
	headerRows,
	groups,
	cellConfigFilter,
	orderDuration = 'asc',
	stateForMakeQueryToStat
) => {
	const res = [];

	const orderedData = orderDataByGroup({ data, groups, orderDuration });

	forEach(orderedData, data => {
		const subGroupNullIsFirstData = orderBy(data.rows, groups[1].value, 'desc');

		subGroupNullIsFirstData.forEach(entity => {

			const { disabledCell, activeCell } = normalizedCellConfig(cellConfigFilter);

			const rows = headerRows.map(date => {
				const row = find(entity.rows, row => row.date === date);

				const formatted = row ? {
					total: row.total_conversions,
					accepted: row.accepted_conversions,
					rejected: row.rejected_conversions,
					approve: coefficients.approve(row.accepted_conversions, row.total_conversions),
					date: row.date,
					difference: row.difference
				} : { date };

				return omit(formatted, disabledCell);
			});

			const prepareData = calculateCoef(rows, activeCell);

			const column = {
				is_total: !!entity.is_total,
				title: entity.is_total ? entity[groups[0].value] : entity[groups[1].value],
				grouping: groups[0].value,
				affiliateRefs: data.groupName,
				linkToStat: queryToStat(stateForMakeQueryToStat, entity, groups)
			};

			res.push({ data: prepareData, column });
		});
	});

	return res;
};

const orderDataByGroup = (options) => {
	const { data, groups, orderDuration } = options;
	const firstGroup = groups[0];

	const prepareDataWithOrderWeight = data.map(entity => {
		const totalConversionsInRow = entity.rows.reduce((acc, entity) => {
			acc += parseInt(entity.total_conversions);
			return acc;
		}, 0);

		return { ...entity, totalConversionsInRow };
	});

	const groupedByGrouping = groupBy(prepareDataWithOrderWeight, firstGroup.value);

	const groupedArray = Object.keys(groupedByGrouping).map(groupName => ({
		groupName: groupName,
		rows: groupedByGrouping[groupName]
	}));

	const orderGroupedData = orderBy(groupedArray, entity => {
		const totalRow = find(entity.rows, {is_total: true});
		return totalRow && totalRow.totalConversionsInRow;
	}, orderDuration);

	return orderGroupedData;
};

const calculateCoef = (data, activeCell) => {
	const totalMaxValue = getMaxValue(data, activeCell);

	return data.map(entity => {

		const coefData = {};

		if (totalMaxValue.total) {
			coefData['total'] = {
				type: 'total',
				value: entity.total,
				coef: Math.floor(entity.total * 100 / totalMaxValue.total)
			};
		}

		if (totalMaxValue.approve) {
			coefData['approve'] = {
				type: 'approve',
				value: entity.approve,
				coef: Math.floor(entity.approve * 100 / totalMaxValue.approve)
			};
		}

		return {
			...entity,
			activeCell,
			coefData
		};
	});
};

const getMaxValue = (data, activeCell) => {
	const res = {};

	const getMaxValueFn = (data, key) => {
		const max = maxBy(data, entity => parseFloat(entity[key]));
		if (!max) return 0;
		return parseFloat(get(max, key));
	};

	activeCell.forEach(keyName => {
		res[keyName] = getMaxValueFn(data, keyName);
	});

	return res;
};

const normalizedCellConfig = cellConfig => {
	const showAllHasActiveStatus = !!(cellConfig.filter(entity => entity.name === 'all' && entity.active)).length;
	const configWithoutAllEntry = cellConfig.filter(entity => entity.name !== 'all');
	const acc = {
		disabledCell: [],
		activeCell: []
	};

	return configWithoutAllEntry.reduce((acc, currentEntity) => {
		// disabled cell
		if (!showAllHasActiveStatus && !currentEntity.active) {
			acc.disabledCell.push(currentEntity.name);
		}
		// activeCell
		if (showAllHasActiveStatus || currentEntity.active) {
			acc.activeCell.push(currentEntity.name);
		}
		return acc;
	}, acc);
};

export default createTable;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates-activity/components/table/index.js